
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarCheck, Send, X, Sparkles } from 'lucide-react';
import { COMPANY_INFO } from '../../constants';
import { useLanguage } from '../../contexts/LanguageContext';

interface ConsultationSectionProps {
  /** Pre-selected service (optional). If omitted, user can pick from dropdown. */
  preselectedService?: string;
  /** Compact inline-button mode (used inside pages to open modal) */
  compact?: boolean;
}

export const ConsultationSection: React.FC<ConsultationSectionProps> = ({
  preselectedService,
  compact = false,
}) => {
  const { t } = useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const tr = t('consultation') as any;

  const services: string[] = [
    tr?.services?.blueStrike || 'CTC Blue Strike',
    tr?.services?.algoEngine || 'CTC Algo Engine',
    tr?.services?.copyService || 'CTC Copy Service',
    tr?.services?.realEstate || 'Divisione Immobiliare',
    tr?.services?.ai || 'AI & Automation',
    tr?.services?.notSure || 'Non so ancora / aiutami a scegliere',
  ];

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    telegram: '',
    selectedService: preselectedService || '',
    needs: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = `Richiesta Consulenza Gratuita - ${formData.selectedService || 'Servizio non specificato'}`;
    const body = [
      `Nome: ${formData.name}`,
      `Email: ${formData.email}`,
      `Telegram: ${formData.telegram || 'N/A'}`,
      `Servizio di interesse: ${formData.selectedService || 'Da definire'}`,
      ``,
      `Esigenze / Note:`,
      formData.needs,
    ].join('%0D%0A');

    window.location.href = `mailto:${COMPANY_INFO.email}?subject=${encodeURIComponent(subject)}&body=${body}`;
    setSubmitted(true);
    setTimeout(() => {
      setIsModalOpen(false);
      setSubmitted(false);
    }, 2000);
  };

  const openModal = () => {
    setFormData({
      name: '',
      email: '',
      telegram: '',
      selectedService: preselectedService || '',
      needs: '',
    });
    setSubmitted(false);
    setIsModalOpen(true);
  };

  /* ────────── COMPACT MODE: just a CTA button ────────── */
  if (compact) {
    return (
      <>
        <button
          onClick={openModal}
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 border border-blue-500/30 rounded-xl text-white text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-blue-500/30"
        >
          <CalendarCheck size={18} />
          {tr?.cta || 'Richiedi Consulenza Gratuita'}
        </button>

        {isModalOpen && createPortal(<ConsultationModal />, document.body)}
      </>
    );
  }

  /* ────────── FULL SECTION MODE ────────── */
  return (
    <>
      <section className="mb-24 px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Background glow Optional, behind the card */}
        <div className="absolute inset-0 pointer-events-none flex justify-center items-center z-[-1]">
          <div className="w-[800px] h-[400px] bg-blue-600/5 rounded-full blur-[120px]" />
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="relative bg-[#151E32] border border-blue-500/20 rounded-3xl p-8 md:p-14 text-center shadow-[0_0_40px_rgba(59,130,246,0.1)] overflow-hidden group hover:border-blue-500/40 transition-all duration-500">
            {/* Top accent line */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 via-cyan-400 to-blue-600" />

            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500/10 border border-blue-500/30 rounded-2xl mb-6 mx-auto mt-2 group-hover:scale-110 transition-transform duration-500">
              <Sparkles className="text-blue-400" size={32} />
            </div>

            <h2 className="text-3xl md:text-4xl font-black text-white mb-4 uppercase tracking-tight">
              {tr?.title || 'Consulenza'}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">
                {tr?.titleHighlight || 'Gratuita'}
              </span>
            </h2>

            <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
              {tr?.subtitle ||
                'Non sai quale servizio fa per te? Raccontaci le tue esigenze e ti aiutiamo a trovare la soluzione su misura per la tua situazione.'}
            </p>

            {/* Bullets – flex-wrap, each pill stays on one line */}
            <div className="flex flex-wrap justify-center gap-3 mb-10">
              {(tr?.bullets || ['Nessun impegno', 'Risposta entro 24h', '100% personalizzata']).map(
                (b: string, i: number) => (
                  <div
                    key={i}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-300 bg-[#0B1120] border border-gray-800 rounded-full px-5 py-2 whitespace-nowrap"
                  >
                    <span className="text-cyan-400 flex-shrink-0">✓</span>
                    <span>{b}</span>
                  </div>
                )
              )}
            </div>

            <button
              onClick={openModal}
              className="inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 rounded-2xl text-white text-base font-bold transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/30 border-0"
            >
              <CalendarCheck size={22} />
              {tr?.cta || 'Richiedi Consulenza Gratuita'}
            </button>
          </div>
        </div>
      </section>

      {isModalOpen && createPortal(<ConsultationModal />, document.body)}
    </>
  );


  /* ────────── MODAL ────────── */
  function ConsultationModal() {
    return (
      <div
        className="fixed inset-0 z-[9999] overflow-y-auto bg-black/80 backdrop-blur-sm"
        onClick={() => setIsModalOpen(false)}
      >
        <div className="flex min-h-full items-center justify-center p-4">
          <div
            className="w-full max-w-md bg-[#151E32] border border-gray-700 rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-[#0B1120] px-6 py-4 border-b border-gray-800 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CalendarCheck className="text-blue-400" size={22} />
                <h3 className="text-lg font-bold text-white">
                  {tr?.modal?.title || 'Richiedi Consulenza Gratuita'}
                </h3>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6">
              {submitted ? (
                <div className="text-center py-8">
                  <div className="text-5xl mb-4">✅</div>
                  <h4 className="text-xl font-bold text-white mb-2">
                    {tr?.modal?.successTitle || 'Richiesta inviata!'}
                  </h4>
                  <p className="text-gray-400 text-sm">
                    {tr?.modal?.successDesc ||
                      'Ti risponderemo entro 24 ore. Controlla il client email.'}
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-gray-400 text-sm mb-5">
                    {tr?.modal?.desc ||
                      'Compila il modulo e ti contatteremo per fissare la consulenza nel momento più comodo per te.'}
                  </p>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Nome */}
                    <div>
                      <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">
                        {tr?.modal?.fields?.name || 'Nome Completo'} *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full bg-[#0B1120] border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none transition-colors"
                        placeholder="Mario Rossi"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full bg-[#0B1120] border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none transition-colors"
                        placeholder="mario@example.com"
                      />
                    </div>

                    {/* Telegram */}
                    <div>
                      <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">
                        {tr?.modal?.fields?.telegram || 'Telegram'} ({tr?.modal?.fields?.optional || 'Opzionale'})
                      </label>
                      <input
                        type="text"
                        name="telegram"
                        value={formData.telegram}
                        onChange={handleChange}
                        className="w-full bg-[#0B1120] border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none transition-colors"
                        placeholder="@username"
                      />
                    </div>

                    {/* Servizio */}
                    <div>
                      <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">
                        {tr?.modal?.fields?.service || 'Servizio di Interesse'}
                      </label>
                      <select
                        name="selectedService"
                        value={formData.selectedService}
                        onChange={handleChange}
                        className="w-full bg-[#0B1120] border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none transition-colors"
                      >
                        <option value="">{tr?.modal?.fields?.servicePlaceholder || '— Seleziona un servizio —'}</option>
                        {services.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>

                    {/* Esigenze */}
                    <div>
                      <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">
                        {tr?.modal?.fields?.needs || 'Le tue esigenze'}
                      </label>
                      <textarea
                        name="needs"
                        rows={3}
                        value={formData.needs}
                        onChange={handleChange}
                        className="w-full bg-[#0B1120] border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none transition-colors resize-none"
                        placeholder={
                          tr?.modal?.fields?.needsPlaceholder ||
                          'Descrivici brevemente la tua situazione e cosa stai cercando...'
                        }
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-bold py-3 rounded-xl transition-all shadow-lg shadow-blue-600/20 flex items-center justify-center gap-2 text-sm mt-2"
                    >
                      <Send size={16} />
                      {tr?.modal?.submit || 'Invia Richiesta'}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
};
